import React, { useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Form,
  DatePicker,
  Select,
  Typography,
  Input,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import PayrollList from "../../payroll/PayrollList";
import PayrollByEmployeeList from "../../payroll/PayrollByEmployeeList";
import { useStore } from "../../../app/stores/store";
import { usePayroll } from "../../../app/hooks/usePayroll";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import PrintPayrollPdf from "./printPayrollPdf";

const CreatePayroll = () => {
  const { Text } = Typography;

  const [search, setSearch] = useState("");

  const [form] = Form.useForm();
  const [preForm] = Form.useForm();

  const { payrollStore } = useStore();
  const {
    fetchEmployeePayroll,
    payrollData,
    employeePayrolData,
    isModalVisible,
    isPreModalVisible,
    setIsModalVisible,
    setIsPreModalVisible,
    isEmployeePayrollModal,
    setIsEmployeePayrollModal,
    durationIn,
    durationOut
  } = payrollStore;
  const { create } = usePayroll();

  const showPreModal = () => setIsPreModalVisible(true);

  const handlePreSubmit = () => {
    preForm.validateFields().then((values) => {
      fetchEmployeePayroll(
        values.employeeType,
        values.startCutoffDate,
        values.endCutoffDate,
        values.date
      );
      setIsPreModalVisible(false);
      setIsModalVisible(true);
    });
  };

  const handleCancel = () => {
    payrollStore.clearPayrollData();
    setIsModalVisible(false);
    setIsPreModalVisible(false);
    setIsEmployeePayrollModal(false);
    form.resetFields();
    preForm.resetFields();
  };

  const handleCancelEmployeePayroll = () => {
    setIsModalVisible(true);
    setIsEmployeePayrollModal(false);
    form.resetFields();
  };

  const handleSave = () => {
    form.validateFields().then(() => {
      // Ensure employeePayrolls exists and is an array
      const payrolls = employeePayrolData.employeePayrolls || [];
      
      // Filter out any empty payroll items if needed
      const filteredPayrolls = payrolls.filter(
        emp => Array.isArray(emp.employeePayrollItems) && emp.employeePayrollItems.length > 0
      );
  
      const payrollObj = {
        ...employeePayrolData,
        // Ensure date is properly formatted
        date: employeePayrolData.date ? new Date(employeePayrolData.date).toISOString() : new Date().toISOString(),
        // Use filtered payrolls if needed, or original payrolls
        employeePayrolls: filteredPayrolls.length > 0 ? filteredPayrolls : payrolls
      };
  
      create(payrollObj).then(() => {
        payrollStore.clearPayrollData();
        setIsModalVisible(false);
        setIsEmployeePayrollModal(false);
        toast.success("Successfully Added New Payroll", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }).catch(error => {
        console.error("Error creating payroll:", error);
        toast.error("Failed to add payroll", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
    }).catch(error => {
      console.error("Form validation failed:", error);
    });
  };

  const handleFormChange = (changedValues, allValues) => {
    const { employeeId, durationIn, durationOut } = allValues;
    if (employeeId && durationIn && durationOut) {
      fetchEmployeePayroll(
        employeeId,
        dayjs(durationIn).format("YYYY-MM-DD"),
        dayjs(durationOut).format("YYYY-MM-DD")
      );
    }
  };

  return (
    <>
      <Button
        type="primary"
        style={{ backgroundColor: "#008C27", borderRadius: 24 }}
        onClick={showPreModal}
      >
        <PlusOutlined />
        CREATE PAYROLL
      </Button>

      {/* First Modal: Basic Info */}
      <Modal
        open={isPreModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={400}
        centered
      >
        <h2 style={{ textAlign: "center", fontWeight: "bold" }}>
          CREATE PAYROLL
        </h2>
        <Form form={preForm} layout="vertical" onFinish={handlePreSubmit}>
          <Form.Item
            label="Employee Type"
            name="employeeType"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Select"
              options={[
                { label: "All", value: "all" },
                { label: "Core", value: "core" },
                { label: "Contractual", value: "contractual" },
              ]}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Cutoff Start"
                name="startCutoffDate"
                rules={[{ required: true }]}
              >
                <DatePicker format="MM-DD-YY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Cutoff End"
                name="endCutoffDate"
                rules={[{ required: true }]}
              >
                <DatePicker format="MM-DD-YY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Date" name="date" rules={[{ required: true }]}>
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Row justify="center" gutter={16}>
            <Col>
              <Button onClick={handleCancel} style={{ borderRadius: 24 }}>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                htmlType="submit"
                type="primary"
                style={{ backgroundColor: "#F47B20", borderRadius: 24 }}
              >
                Create
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Main Payroll Modal */}
      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Row justify="space-between" align="middle">
          <Col style={{ flex: 1, textAlign: "center" }}>
            <h1>CREATE PAYROLL</h1>
          </Col>
          <Col>
            <PrintPayrollPdf />
          </Col>
        </Row>

        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
          onFinish={handleSave}
        >
          <Row style={{ marginBottom: 16, justifyContent: "space-between" }} gutter={16}>
            <Col>
              <Row gutter={16}>
                <Col>Search</Col>
                <Col>
                  <Input
                    placeholder="input employee name here"
                    value={search}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearch(value);

                      const values = preForm.getFieldsValue(); // get cutoff fields from preModal

                      if (
                        values?.employeeType &&
                        values?.startCutoffDate &&
                        values?.endCutoffDate &&
                        values?.date
                      ) {
                        fetchEmployeePayroll(
                          values.employeeType,
                          dayjs(values.startCutoffDate)
                            .startOf("day")
                            .toISOString(),
                          dayjs(values.endCutoffDate)
                            .startOf("day")
                            .toISOString(),
                          dayjs(values.date).startOf("day").toISOString(),
                          value.trim() === "" ? undefined : value.trim()
                        );
                      }
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <Row gutter={16}>
                <Col>Cutoff Dates:</Col>
                <Col>
                  {durationIn &&
                    durationOut && (
                      <>
                        {dayjs(durationIn).format(
                          "MMMM D"
                        )}
                        -
                        {dayjs(durationOut).format(
                          "MMMM D YYYY"
                        )}
                      </>
                    )}
                </Col>
              </Row>
            </Col>
          </Row>

          <PayrollList />

          <Row justify="end" gutter={16}>
            <Col>
              <Text style={{ color: "#F47B20" }}>TOTAL</Text>
            </Col>
            <Col>
              <Text
                style={{
                  color: "#fff",
                  backgroundColor: "#6D6D6D",
                  padding: 8,
                }}
              >
                ₱{employeePayrolData?.totalSalary?.toFixed(2)}
              </Text>
            </Col>
          </Row>

          <Row justify="center" gutter={16}>
            <Col>
              <Button onClick={handleCancel} style={{ borderRadius: 24 }}>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                style={{ backgroundColor: "#F47B20", borderRadius: 24 }}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        open={isEmployeePayrollModal}
        onCancel={handleCancelEmployeePayroll}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Col style={{ flex: 1, textAlign: "center" }}>
          <h1>Payroll Breakdown</h1>
        </Col>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Text strong>EMP NUMBER</Text>
            <Input
              value={payrollData?.employeeNumber}
              readOnly
              bordered
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </Col>
          <Col span={10}>
            <Text strong>EMPLOYEE NAME</Text>
            <Input
              value={payrollData?.employeeName}
              readOnly
              bordered
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </Col>
          <Col span={4}>
            <Text strong>DURATION</Text>
            <Input
              value={payrollData?.durationIn}
              readOnly
              bordered
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </Col>
          <Col span={4} style={{ marginTop: 24 }}>
            <Input
              value={payrollData?.durationOut}
              readOnly
              bordered
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </Col>
        </Row>

        <PayrollByEmployeeList />

        <Row justify="end" gutter={16}>
          <Col>
            <Text style={{ color: "#F47B20" }}>TOTAL</Text>
          </Col>
          <Col>
            <Text
              style={{
                color: "#fff",
                backgroundColor: "#6D6D6D",
                padding: 8,
              }}
            >
             ₱{payrollData?.netPay?.toFixed(2)}
            </Text>
          </Col>
        </Row>

        <Row justify="center" gutter={16}>
          <Col>
            <Button
              onClick={handleCancelEmployeePayroll}
              style={{ borderRadius: 24 }}
            >
              Cancel
            </Button>
          </Col>
          {/* <Col>
            <Button
              type="primary"
              onClick={handleSave}
              style={{ backgroundColor: "#F47B20", borderRadius: 24 }}
            >
              Save
            </Button>
          </Col> */}
        </Row>
      </Modal>
    </>
  );
};

export default observer(CreatePayroll);
